/**
 * Permission matrix — spec §3.4, verbatim.
 *
 * Imported by the server (`authorize()` middleware) AND the web app
 * (`useCan()` hook). Frontend hiding is UX, not security — the server always
 * re-checks.
 */
export const PERMISSIONS = {
    WORKSPACE_VIEW: 'workspace.view',
    WORKSPACE_UPDATE: 'workspace.update',
    WORKSPACE_DELETE: 'workspace.delete',
    WORKSPACE_TRANSFER_OWNERSHIP: 'workspace.transferOwnership',
    WORKSPACE_EXPORT: 'workspace.export',
    USER_INVITE: 'user.invite',
    USER_LIST: 'user.list',
    USER_UPDATE: 'user.update',
    USER_UPDATE_ROLE: 'user.updateRole',
    USER_DEACTIVATE: 'user.deactivate',
    TEAM_CREATE: 'team.create',
    TEAM_UPDATE: 'team.update',
    TEAM_DELETE: 'team.delete',
    PROJECT_CREATE: 'project.create',
    PROJECT_VIEW: 'project.view',
    PROJECT_UPDATE: 'project.update',
    PROJECT_ARCHIVE: 'project.archive',
    PROJECT_DELETE: 'project.delete',
    PROJECT_MANAGE_MEMBERS: 'project.manageMembers',
    PROJECT_MANAGE_STATUSES: 'project.manageStatuses',
    TASK_CREATE: 'task.create',
    TASK_VIEW: 'task.view',
    TASK_UPDATE: 'task.update',
    TASK_UPDATE_ANY: 'task.updateAny',
    TASK_ASSIGN: 'task.assign',
    TASK_CHANGE_STATUS: 'task.changeStatus',
    TASK_APPROVE_REVIEW: 'task.approveReview',
    TASK_DELETE: 'task.delete',
    TASK_ARCHIVE: 'task.archive',
    SPRINT_MANAGE: 'sprint.manage',
    LABEL_MANAGE: 'label.manage',
    LEAVE_REQUEST: 'leave.request',
    LEAVE_APPROVE: 'leave.approve',
    LEAVE_VIEW_TEAM: 'leave.viewTeam',
    COMMENT_CREATE: 'comment.create',
    COMMENT_UPDATE: 'comment.update',
    COMMENT_DELETE: 'comment.delete',
    NOTE_CREATE: 'note.create',
    NOTE_VIEW_PRIVATE: 'note.viewPrivate',
    NOTE_VIEW_PROJECT: 'note.viewProject',
    NOTE_VIEW_TASK: 'note.viewTask',
    NOTE_DELETE: 'note.delete',
    TIME_LOG: 'time.log',
    TIME_VIEW_OWN: 'time.viewOwn',
    TIME_VIEW_TEAM: 'time.viewTeam',
    TIME_APPROVE: 'time.approve',
    REPORT_VIEW_OWN: 'report.viewOwn',
    REPORT_VIEW_TEAM: 'report.viewTeam',
    REPORT_VIEW_WORKSPACE: 'report.viewWorkspace',
    AUTOMATION_MANAGE: 'automation.manage',
    TEMPLATE_MANAGE: 'template.manage',
    SETTINGS_INTEGRATIONS: 'settings.integrations',
    AUDIT_VIEW: 'audit.view',
};
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);
/**
 * How broadly a role holds a permission.
 *  all               — unrestricted inside the workspace
 *  scope             — only teams/projects they lead
 *  own               — only resources they created
 *  own_assigned      — resources they created OR are assigned to
 *  assigned          — only resources they are assigned to
 *  own_recent        — own, and only inside the delete grace window
 *  member_of         — only projects they are a member of
 *  shared_only       — only explicitly guest-shared projects
 *  reviewer_role     — needs the `reviewer` (or `lead`) project role
 *  project_lead_role — needs the `lead` project role
 *  if_enabled        — allowed only when the project enables it (guest comments)
 *  none              — denied
 */
export const GRANTS = [
    'all',
    'scope',
    'own',
    'own_assigned',
    'assigned',
    'own_recent',
    'member_of',
    'shared_only',
    'reviewer_role',
    'project_lead_role',
    'if_enabled',
    'none',
];
const D = 'none';
/** Verbatim transcription of the §3.4 table. */
export const PERMISSION_MATRIX = {
    'workspace.view': { owner: 'all', admin: 'all', manager: 'all', member: 'all', guest: 'all' },
    'workspace.update': { owner: 'all', admin: 'all', manager: D, member: D, guest: D },
    'workspace.delete': { owner: 'all', admin: D, manager: D, member: D, guest: D },
    'workspace.transferOwnership': { owner: 'all', admin: D, manager: D, member: D, guest: D },
    'workspace.export': { owner: 'all', admin: 'all', manager: D, member: D, guest: D },
    'user.invite': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'user.list': { owner: 'all', admin: 'all', manager: 'all', member: 'all', guest: D },
    'user.update': { owner: 'all', admin: 'all', manager: 'scope', member: 'own', guest: D },
    'user.updateRole': { owner: 'all', admin: 'all', manager: D, member: D, guest: D },
    'user.deactivate': { owner: 'all', admin: 'all', manager: D, member: D, guest: D },
    'team.create': { owner: 'all', admin: 'all', manager: D, member: D, guest: D },
    'team.update': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'team.delete': { owner: 'all', admin: 'all', manager: D, member: D, guest: D },
    'project.create': { owner: 'all', admin: 'all', manager: 'all', member: D, guest: D },
    'project.view': {
        owner: 'all',
        admin: 'all',
        manager: 'all',
        member: 'member_of',
        guest: 'shared_only',
    },
    'project.update': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'project.archive': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'project.delete': { owner: 'all', admin: 'all', manager: D, member: D, guest: D },
    'project.manageMembers': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'project.manageStatuses': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'task.create': { owner: 'all', admin: 'all', manager: 'all', member: 'all', guest: D },
    'task.view': {
        owner: 'all',
        admin: 'all',
        manager: 'all',
        member: 'member_of',
        guest: 'shared_only',
    },
    'task.update': {
        owner: 'all',
        admin: 'all',
        manager: 'scope',
        member: 'own_assigned',
        guest: D,
    },
    'task.updateAny': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    // V3 R1 — delegation. `own` meant only the reporter could reassign, so a
    // senior dev handed a task could not hand it on to a junior. `own_assigned`
    // lets anyone currently on the task delegate it; combined with the cumulative
    // grant fallback in `hasGrant()`, leads and managers can always delegate.
    'task.assign': { owner: 'all', admin: 'all', manager: 'all', member: 'own_assigned', guest: D },
    'task.changeStatus': {
        owner: 'all',
        admin: 'all',
        manager: 'scope',
        member: 'assigned',
        guest: D,
    },
    'task.approveReview': {
        owner: 'all',
        admin: 'all',
        manager: 'scope',
        member: 'reviewer_role',
        guest: D,
    },
    'task.delete': { owner: 'all', admin: 'all', manager: 'scope', member: 'own_recent', guest: D },
    'task.archive': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'sprint.manage': {
        owner: 'all',
        admin: 'all',
        manager: 'scope',
        member: 'project_lead_role',
        guest: D,
    },
    'label.manage': { owner: 'all', admin: 'all', manager: 'all', member: D, guest: D },
    'leave.request': { owner: 'all', admin: 'all', manager: 'all', member: 'all', guest: D },
    'leave.approve': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'leave.viewTeam': { owner: 'all', admin: 'all', manager: 'scope', member: 'own', guest: D },
    'comment.create': {
        owner: 'all',
        admin: 'all',
        manager: 'all',
        member: 'all',
        guest: 'if_enabled',
    },
    'comment.update': { owner: 'all', admin: 'all', manager: 'own', member: 'own', guest: 'own' },
    'comment.delete': { owner: 'all', admin: 'all', manager: 'scope', member: 'own', guest: 'own' },
    'note.create': { owner: 'all', admin: 'all', manager: 'all', member: 'all', guest: D },
    'note.viewPrivate': { owner: 'own', admin: 'own', manager: 'own', member: 'own', guest: 'own' },
    'note.viewProject': {
        owner: 'all',
        admin: 'all',
        manager: 'all',
        member: 'member_of',
        guest: D,
    },
    // follows task.view exactly (§3.4 note row)
    'note.viewTask': {
        owner: 'all',
        admin: 'all',
        manager: 'all',
        member: 'member_of',
        guest: 'shared_only',
    },
    'note.delete': { owner: 'all', admin: 'all', manager: 'scope', member: 'own', guest: D },
    'time.log': { owner: 'all', admin: 'all', manager: 'all', member: 'all', guest: D },
    'time.viewOwn': { owner: 'all', admin: 'all', manager: 'all', member: 'all', guest: D },
    'time.viewTeam': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'time.approve': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'report.viewOwn': { owner: 'all', admin: 'all', manager: 'all', member: 'all', guest: D },
    'report.viewTeam': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'report.viewWorkspace': { owner: 'all', admin: 'all', manager: D, member: D, guest: D },
    'automation.manage': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'template.manage': { owner: 'all', admin: 'all', manager: 'scope', member: D, guest: D },
    'settings.integrations': { owner: 'all', admin: 'all', manager: D, member: D, guest: D },
    'audit.view': { owner: 'all', admin: 'all', manager: D, member: D, guest: D },
};
/** Grant a role holds for a permission. */
export function grantFor(role, permission) {
    return PERMISSION_MATRIX[permission]?.[role] ?? 'none';
}
/** Coarse check — "could this role ever do this?". Used to hide UI. */
export function roleHasPermission(role, permission) {
    return grantFor(role, permission) !== 'none';
}
/** Every permission a role holds at any grant level. */
export function permissionsForRole(role) {
    return ALL_PERMISSIONS.filter((p) => roleHasPermission(role, p));
}
export const ROLE_PERMISSIONS = {
    owner: new Set(permissionsForRole('owner')),
    admin: new Set(permissionsForRole('admin')),
    manager: new Set(permissionsForRole('manager')),
    member: new Set(permissionsForRole('member')),
    guest: new Set(permissionsForRole('guest')),
};
/**
 * Project-role grants layered on top of the system role (§3.2).
 * Effective permission = MAX(system grant, project grant) — except guests,
 * who are hard-capped at viewer + optional comment no matter the project role.
 */
export const PROJECT_ROLE_PERMISSIONS = {
    lead: [
        PERMISSIONS.PROJECT_VIEW,
        PERMISSIONS.PROJECT_UPDATE,
        PERMISSIONS.PROJECT_ARCHIVE,
        PERMISSIONS.PROJECT_MANAGE_MEMBERS,
        PERMISSIONS.PROJECT_MANAGE_STATUSES,
        PERMISSIONS.TASK_CREATE,
        PERMISSIONS.TASK_VIEW,
        PERMISSIONS.TASK_UPDATE,
        PERMISSIONS.TASK_UPDATE_ANY,
        PERMISSIONS.TASK_ASSIGN,
        PERMISSIONS.TASK_CHANGE_STATUS,
        PERMISSIONS.TASK_APPROVE_REVIEW,
        PERMISSIONS.TASK_DELETE,
        PERMISSIONS.TASK_ARCHIVE,
        PERMISSIONS.SPRINT_MANAGE,
        PERMISSIONS.COMMENT_CREATE,
        PERMISSIONS.COMMENT_DELETE,
        PERMISSIONS.NOTE_CREATE,
        PERMISSIONS.NOTE_VIEW_PROJECT,
        PERMISSIONS.NOTE_VIEW_TASK,
        PERMISSIONS.NOTE_DELETE,
        PERMISSIONS.TIME_LOG,
        PERMISSIONS.AUTOMATION_MANAGE,
        PERMISSIONS.TEMPLATE_MANAGE,
        PERMISSIONS.REPORT_VIEW_TEAM,
    ],
    contributor: [
        PERMISSIONS.PROJECT_VIEW,
        PERMISSIONS.TASK_CREATE,
        PERMISSIONS.TASK_VIEW,
        PERMISSIONS.TASK_UPDATE,
        PERMISSIONS.TASK_CHANGE_STATUS,
        PERMISSIONS.COMMENT_CREATE,
        PERMISSIONS.NOTE_CREATE,
        PERMISSIONS.NOTE_VIEW_PROJECT,
        PERMISSIONS.NOTE_VIEW_TASK,
        PERMISSIONS.TIME_LOG,
    ],
    reviewer: [
        PERMISSIONS.PROJECT_VIEW,
        PERMISSIONS.TASK_CREATE,
        PERMISSIONS.TASK_VIEW,
        PERMISSIONS.TASK_UPDATE,
        PERMISSIONS.TASK_CHANGE_STATUS,
        PERMISSIONS.TASK_APPROVE_REVIEW,
        PERMISSIONS.COMMENT_CREATE,
        PERMISSIONS.NOTE_CREATE,
        PERMISSIONS.NOTE_VIEW_PROJECT,
        PERMISSIONS.NOTE_VIEW_TASK,
        PERMISSIONS.TIME_LOG,
    ],
    viewer: [
        PERMISSIONS.PROJECT_VIEW,
        PERMISSIONS.TASK_VIEW,
        PERMISSIONS.NOTE_VIEW_PROJECT,
        PERMISSIONS.NOTE_VIEW_TASK,
    ],
};
/** The hard ceiling a guest can reach regardless of project role (§3.2). */
export const GUEST_CEILING = new Set([
    PERMISSIONS.WORKSPACE_VIEW,
    PERMISSIONS.PROJECT_VIEW,
    PERMISSIONS.TASK_VIEW,
    PERMISSIONS.NOTE_VIEW_TASK,
    PERMISSIONS.NOTE_VIEW_PRIVATE,
    PERMISSIONS.COMMENT_CREATE,
    PERMISSIONS.COMMENT_UPDATE,
    PERMISSIONS.COMMENT_DELETE,
]);
/** Permission granted purely by holding a project role. */
export function projectRoleGrants(role, permission) {
    return PROJECT_ROLE_PERMISSIONS[role].includes(permission);
}
/**
 * Effective yes/no for a user given their system role and (optionally) their
 * role on the project the resource belongs to. Resource-level rules (own /
 * assigned / scope) are applied by the server on top of this.
 */
export function canWithProjectRole(systemRole, permission, projectRole) {
    if (systemRole === 'guest') {
        if (!GUEST_CEILING.has(permission))
            return false;
        return roleHasPermission('guest', permission);
    }
    if (roleHasPermission(systemRole, permission))
        return true;
    return projectRole ? projectRoleGrants(projectRole, permission) : false;
}
//# sourceMappingURL=permissions.js.map